import { useTranslation } from 'react-i18next';
import { formatNumber } from '@/lib/format';
import type { ItemDurationSettings } from '@/types';

interface DurationSummaryProps {
  durations: ItemDurationSettings | null;
  onOpenSettings: () => void;
}

export default function DurationSummary({ durations, onOpenSettings }: DurationSummaryProps) {
  const { t } = useTranslation();

  const renderValue = (hours: number | null | undefined) =>
    hours == null ? (
      <span className="text-sm font-medium text-foreground-400">{t('items.duration.notSet')}</span>
    ) : (
      <span className="text-sm font-semibold text-foreground-950">
        {formatNumber(hours)} <span className="text-xs font-medium text-foreground-500">{t('items.duration.unit')}</span>
      </span>
    );

  const stats = [
    { key: 'normal', icon: 'ri-time-line', label: t('items.duration.normal'), hours: durations?.normalHours },
    { key: 'urgent', icon: 'ri-flashlight-line', label: t('items.duration.urgent'), hours: durations?.urgentHours },
  ];

  return (
    <section className="rounded-lg border border-background-200 bg-background-50 p-4 shadow-xs md:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-600">
            <i className="ri-timer-flash-line text-xl leading-none" aria-hidden="true" />
          </span>
          <div>
            <h2 className="text-sm font-semibold text-foreground-950">{t('items.duration.title')}</h2>
            <p className="mt-0.5 text-xs text-foreground-500">{t('items.duration.scope')}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onOpenSettings}
          className="inline-flex h-10 items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer"
        >
          <i className="ri-settings-3-line text-base" aria-hidden="true" />
          {t('items.duration.settings')}
        </button>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {stats.map((stat) => (
          <div
            key={stat.key}
            className="flex items-center gap-3 rounded-md border border-background-200 bg-background-100/60 px-4 py-3"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-50 text-foreground-500">
              <i className={`${stat.icon} text-lg leading-none`} aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <p className="text-xs text-foreground-500">{stat.label}</p>
              <div className="mt-0.5">{renderValue(stat.hours)}</div>
            </div>
          </div>
        ))}
      </div>

      <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
        <i className="ri-information-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
        {t('items.duration.note')}
      </p>
    </section>
  );
}